"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Same screens as Customer, vendor mode",
    rule: "Add, list, detail, bulk import, and bulk opening balance reuse the Customer forms with isVendor on. Permission resource is Vendors. Numbering uses Vendor Number Configuration on Add Vendor",
  },
  {
    topic: "Subscription gate",
    rule: "Only creating a vendor, single or bulk, is limited by your plan (same Customer plan feature on the shared API). Editing, deleting, linking, addresses, and every dropdown are not",
  },
  {
    topic: "Display name uniqueness",
    rule: "Checked without regard to letter case. Two Vendors cannot share a display name. A Customer and a Vendor can",
  },
  {
    topic: "Currency locks after saving",
    rule: "Required when you create the vendor. The picker is Settings → Currencies. Cannot be changed once saved",
  },
  {
    topic: "Restriction documents are purchase only",
    rule: "Credit limit and overdue checks on this party use Purchase Order, GRPO, and Bill — not Quote, Sales Order, Delivery Challan, or Invoice. Company defaults live under Settings → Credit limits until this vendor overrides them",
  },
  {
    topic: "Opening Balance sign chooses the settlement module",
    rule: "Positive Opening Balance (you owe them) is paid in Payments Made as (Vendor opening balance). Negative Opening Balance (they have credit with you) is applied to Bills with Apply Credit / Pay Against Bill / Auto Map Opening Balance — it is not a Payment Made. You cannot flip the sign or reduce below already-applied amounts until those applications are removed",
  },
  {
    topic: "Negative credit is applied branch by branch",
    rule: "Opening Balance lives on each branch row even when Branch Wise Credit Limit is off. Pay Against Bill on the vendor detail only lists branches whose Opening Balance is still negative. Apply Credit on a bill is locked to that bill's branch. Auto Map on the vendor maps only this vendor; Auto Map on the Bills list maps remaining credit across vendors",
  },
  {
    topic: "Unused Credit is Debit Notes",
    rule: "The vendor brief drawer Unused Credit is the sum of Debit Note available balances. It is not negative Opening Balance and not Unused Amount on a Payment Made",
  },
  {
    topic: "No portal and no 360 View",
    rule: "Vendor list hides Portal Access, Portal Accepted, and Created From. Detail has no Resend Invite and no 360 View. Account Type is Accounts Payable",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. The core identity fields" },
      { heading: "2. Tax, currency, and profile details" },
      { heading: "3. Addresses" },
      { heading: "4. Branches — credit limit, payment terms, balances" },
      { heading: "5. Opening balance — you owe them vs they have credit" },
      { heading: "6. Credit limit and unused credit" },
      { heading: "7. Linking a Vendor to a Customer" },
      { heading: "8. Adding one at a time vs Bulk Add" },
      { heading: "9. Viewing vendor details" },
      { heading: "10. Deleting a vendor" },
      { heading: "11. Where a vendor gets picked elsewhere" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function VendorsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Vendors
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone recording who this company buys from — then tracking
            what you still owe them per branch
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Vendor</B> is the purchase-side party record. It uses the
            same screens as Customer, in vendor mode: GST and tax, a{" "}
            <B>currency</B>, per-branch <B>Opening Balance</B> and{" "}
            <B>Current Balance</B>, and credit-limit rules on{" "}
            <B>Purchase Order</B>, <B>GRPO</B>, and <B>Bill</B>. When a
            supplier is also a customer, you <B>link</B> the two records.
            There is no customer portal on this side.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. The core identity fields
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            These sit at the top of Add Vendor. Status after save is{" "}
            <B>Active</B>. Mark Vendor As on the list or detail switches{" "}
            <B>Active / Inactive</B>.
          </p>
          <Definitions
            items={[
              {
                term: "Vendor ID",
                desc: (
                  <>
                    Filled from <B>Vendor Number Configuration</B> on Add
                    (prefix, next number, auto-generate). Change numbering
                    there — not as a one-off on the form.
                  </>
                ),
              },
              {
                term: "Primary Contact Name",
                desc: (
                  <>
                    Labelled <B>BP Primary Contact Name</B>. First name is
                    required, last name is optional.
                  </>
                ),
              },
              {
                term: "Business Partner Type",
                desc: <>Business or Individual. Required.</>,
              },
              {
                term: "Category",
                desc: <>Goods, Services, or both.</>,
              },
              {
                term: "Company Name and Vendor Display Name",
                desc: (
                  <>
                    Both required. Display Name must be unique among vendors
                    (case-insensitive). A customer may reuse the same name.
                  </>
                ),
              },
              {
                term: "Account Type",
                desc: (
                  <>
                    Always <B>Accounts Payable</B>. You do not pick this.
                  </>
                ),
              },
              {
                term: "Primary Email, phone, logo, Code",
                desc: (
                  <>
                    Email is labelled <B>BP Primary Email</B> (optional —
                    vendors have no portal). Phone is numbers only. Logo one
                    image, 5 MB. Code is optional free text, separate from
                    Vendor ID.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Vendor with Vendor ID, display name, Category, and Accounts Payable." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Tax, currency, and profile details
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Under <B>Other Details</B> on the form — same GST treatments as
            Customer. Currency is required and locks after save (
            <B>Settings → Currencies</B>).
          </p>
          <Callout type="note">
            GST Treatment, GSTIN, PAN, and tax fields follow the same rules
            as Customer. This page does not repeat that catalogue. Place of
            supply vs company state still drives CGST / SGST / IGST on
            purchase documents that use this vendor.
          </Callout>
          <Screenshot caption="Screenshot: Other Details with GST Treatment and currency locked after save." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Addresses
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            One billing address, one default shipping, plus additional
            shipping rows. Purchase documents pick from these. Attachments
            on the form: up to <B>3 files, 5 MB</B> each.
          </p>
          <Screenshot caption="Screenshot: billing and shipping addresses on Add Vendor." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Branches — credit limit, payment terms, balances
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Every active branch gets a row: <B>Opening Balance</B>,{" "}
            <B>Current Balance</B>, Status. Turning Branch Wise Credit Limit
            or Payment Terms off does not merge Opening Balance into one
            company total.
          </p>
          <Definitions
            items={[
              {
                term: "Restriction Document",
                desc: (
                  <>
                    Which documents check the <B>credit limit amount</B>:{" "}
                    <B>Purchase Order</B>, <B>GRPO</B>, <B>Bill</B>. Company
                    defaults: <B>Settings → Credit limits</B> until this
                    vendor overrides them.
                  </>
                ),
              },
              {
                term: "Restriction Payment Terms Document",
                desc: (
                  <>
                    If a type is listed, you cannot <B>create</B> that
                    document while this vendor has an <B>Overdue</B> bill.
                    That is the overdue block.
                  </>
                ),
              },
              {
                term: "Changing Opening Balance later",
                desc: (
                  <>
                    Adjusts Current Balance by the <B>difference</B> — it
                    does not wipe history.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: vendor branch table with Opening Balance, Current Balance, Restriction Document listing Purchase Order, GRPO, Bill." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Opening balance — you owe them vs they have credit
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Each branch row has an <B>Opening Balance</B>. The sign chooses
            which module settles it later.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div className="border border-[#F4C7A8] rounded-[12px] p-4 bg-[#FFF7F2]">
              <p className="text-[12px] geist-medium uppercase tracking-wide text-[#DF5D01] mb-1">
                Positive Opening Balance
              </p>
              <p className="text-[15px] sentient-medium text-[#111827] mb-1">
                You owe them
              </p>
              <p className="text-[13px] geist-regular text-[#374151] leading-relaxed">
                Stored as an <B>opening payable</B>. Pay it in{" "}
                <B>Payments Made</B> like a bill. The payment picker shows{" "}
                <B>(Vendor opening balance)</B>. Approving that payment
                reduces remaining opening payable and Current Balance.
              </p>
            </div>
            <div className="border border-[#E5E5E5] rounded-[12px] p-4 bg-[#EFF8FF]">
              <p className="text-[12px] geist-medium uppercase tracking-wide text-[#1785B6] mb-1">
                Negative Opening Balance
              </p>
              <p className="text-[15px] sentient-medium text-[#111827] mb-1">
                They have credit with you
              </p>
              <p className="text-[13px] geist-regular text-[#374151] leading-relaxed">
                Stored as <B>vendor credit</B>. This is <B>not</B> a Payment
                Made. When any branch is negative, vendor detail shows{" "}
                <B>Auto Map Opening Balance</B> and <B>Pay Against Bill</B>.
                On an unpaid bill for that branch, the button is{" "}
                <B>Apply Credit</B>.
              </p>
            </div>
          </div>
          <Definitions
            items={[
              {
                term: "Current Balance",
                desc: (
                  <>
                    Starts from Opening Balance, then moves with bills,
                    payments made, debit notes, and OB applications. Detail
                    shows <B>Total Opening Balance</B> and{" "}
                    <B>Total Current Balance</B> as the sum of branch rows.
                  </>
                ),
              },
              {
                term: "Pay Against Bill (vendor detail)",
                desc: (
                  <>
                    On the vendor header when <B>any</B> branch Opening
                    Balance is still negative. Opens Apply Opening Balance
                    to Bills. The <B>Branch</B> picker lists only those
                    negative-OB branches. This does not create a Payment
                    Made.
                  </>
                ),
              },
              {
                term: "Auto Map Opening Balance (vendor detail)",
                desc: (
                  <>
                    Same header. Maps this vendor&apos;s remaining credit
                    onto open bills, oldest due date first. Confirm modal:{" "}
                    <B>Auto Map Opening Balance to Bills</B>. This vendor
                    only. The Bills list TOOLS action maps remaining credit{" "}
                    <B>across vendors</B>.
                  </>
                ),
              },
              {
                term: "Apply Credit (bill detail)",
                desc: (
                  <>
                    On an unpaid bill whose branch still has remaining
                    vendor credit. The modal locks the branch to the
                    bill&apos;s branch. It is <B>not</B> Apply to Bill on a
                    Debit Note.
                  </>
                ),
              },
              {
                term: "Opening balance payments tab",
                desc: (
                  <>
                    Under Transactions when history exists. Positive-side
                    source is Payment Made. Negative-side source is Bill
                    (credit mapped onto bills).
                  </>
                ),
              },
              {
                term: "Add Vendor Opening Balance (list)",
                desc: (
                  <>
                    IMPORT &amp; EXPORT on the vendor list. Vendors must
                    already exist. Branch names as columns. Same sign rules
                    once those amounts exist.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            You cannot lower a branch&apos;s Opening Balance below what has
            already been applied, and you cannot flip it from payable to
            credit (or back) while those applications exist. For a positive
            balance, remove the Payment Made applications first. For a
            negative balance, remove the bill credit mappings first.
          </Callout>
          <Screenshot caption="Screenshot: vendor branch Opening Balance +12,000 vs −8,000, with Auto Map Opening Balance and Pay Against Bill on the detail header." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Credit limit and unused credit
          </h3>
          <Definitions
            items={[
              {
                term: "Credit Limit",
                desc: (
                  <>
                    Overall, or per branch when Branch Wise Credit Limit is
                    on. Checked on PO / GRPO / Bill only if those types are
                    in Restriction Document.
                  </>
                ),
              },
              {
                term: "Unused Credit (vendor brief)",
                desc: (
                  <>
                    Sum of <B>Debit Note</B> available balances for this
                    vendor. Not negative Opening Balance, and not Unused
                    Amount on a Payment Made. The label next to it can still
                    read Outstanding Receivable — that wording is not
                    re-titled for vendors.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: vendor drawer Unused Credit next to Current Balance." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Linking a Vendor to a Customer
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Checkbox <B>Linked to the Customer</B> plus a Customer Name
            picker. Inverse of Customer&apos;s Linked to the Vendor. One
            link each; unlink first to pick someone else. Both sides update
            together.
          </p>
          <Definitions
            items={[
              {
                term: "On a linked vendor detail",
                desc: (
                  <>
                    Extra <B>Sales Transactions</B> for the customer side.
                  </>
                ),
              },
              {
                term: "On a linked customer detail",
                desc: (
                  <>
                    Extra <B>Purchase Transactions</B> for this vendor.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Linked to the Customer with a customer picked." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Adding one at a time vs Bulk Add
          </h3>
          <Definitions
            items={[
              {
                term: "Add",
                desc: <>Primary CTA → /vendors/new.</>,
              },
              {
                term: "Add Bulk Vendor",
                desc: (
                  <>
                    IMPORT &amp; EXPORT. Same wizard as customers; payload
                    isVendor. Labels use VENDOR NO.
                  </>
                ),
              },
              {
                term: "Export Vendors",
                desc: <>Same toolbar. File name Vendors_Export.</>,
              },
              {
                term: "ANALYTICS",
                desc: (
                  <>
                    <B>Top Vendor by Purchase</B> when you have that
                    analytics view right.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: vendor list IMPORT & EXPORT with Add Bulk Vendor and Add Vendor Opening Balance." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Viewing vendor details
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Header: <B>Mark Vendor As</B>, Edit, Delete,{" "}
            <B>General Ledger</B>, and Auto Map / Pay Against Bill when a
            branch Opening Balance is negative. No 360 View, no portal
            invite.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Transactions cover Purchase Order, GRPO, Bill, Payment Made,
            Debit Note, and Opening balance payments when those exist.
          </p>
          <Screenshot caption="Screenshot: vendor detail header with Pay Against Bill and purchase transaction tabs." />

          <h3
            id="section-0-sub-9"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            10. Deleting a vendor
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Blocked if any linked documents exist: bills, POs, GRPOs, debit
            notes, payments made, recurring bills, item preferred vendor, or
            a link to a customer. Unapplied Opening Balance alone does not
            block; applied OB does, via those documents. Deactivate is
            blocked while open purchase documents exist (PO, GRPO, Bill,
            Recurring Bill, Recurring Expense, Debit Note — excluding Paid
            and Cancelled bills).
          </p>
          <Screenshot caption="Screenshot: delete blocked because the vendor is used on a bill." />

          <h3
            id="section-0-sub-10"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            11. Where a vendor gets picked elsewhere
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Purchase Order, GRPO, Bill, Payment Made, Debit Note, Recurring
            Bill, Expense (optional party), Recurring Expense. Currency,
            payment terms, and addresses come from this record onto those
            documents at create time.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/customer-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Customer <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              The sales-side record you can link to this vendor
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/bills"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Bills <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Apply Credit on a bill is this vendor&apos;s negative opening
              balance
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/payments-made"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Payments Made <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where positive opening payable is collected
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/debit-notes"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Debit Notes <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Unused Credit on the vendor drawer is debit-note balance
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
